/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useMemo, useRef } from 'react';

import { cssMap, jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

import { aiSuggestionsMessages } from '@atlaskit/editor-common/ai-messages';
import { BLOCK_CONTROL_UI_CONTEXT } from '@atlaskit/editor-common/block-controls/block-control-ui-context';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import type { CommonComponentProps } from '@atlaskit/editor-ui-control-model/types';

import type { AiSuggestionsPlugin } from '../../aiSuggestionsPluginType';
import { aiSuggestionsPluginKey } from '../../pm-plugins/main';
import { getUniqueAvatarProfiles } from '../utils/getUniqueAvatarProfiles';

import { SuggestionAvatarIndicator } from './SuggestionAvatarIndicator';
import { SuggestionTooltip } from './SuggestionTooltip';

const styles = cssMap({
	container: {
		position: 'relative',
		pointerEvents: 'all',
	},
});

export type SuggestionRightSurfaceButtonProps = CommonComponentProps & {
	api?: ExtractInjectionAPI<AiSuggestionsPlugin>;
};

export function SuggestionRightSurfaceButton({
	api,
	surfaceContext,
}: SuggestionRightSurfaceButtonProps): JSX.Element | null {
	const { formatMessage } = useIntl();
	const context = surfaceContext?.get(BLOCK_CONTROL_UI_CONTEXT);
	const localId = context?.targetNode.node.attrs?.localId as string | undefined;
	// True while block-controls has this block (or a node nested inside it, e.g. a paragraph in
	// a panel) as the active node.
	const isBlockActive = Boolean(
		context?.activeNode && context.rootNode.pos === context.targetNode.pos,
	);

	// Derived primitives (rather than the state maps) so the hook's shallow
	// equality check can skip re-renders that don't affect this button.
	const {
		firstSuggestionId,
		isAnySuggestionSelected,
		isSelected,
		profiles,
		shortDescription,
		suggestionsCount,
	} = useSharedPluginStateWithSelector(api, ['aiSuggestions'], (state) => {
		const { suggestionIdsByBaseLocalId, suggestionById, selectedSuggestion } =
			state?.aiSuggestionsState ?? {};
		const suggestionIds = localId ? suggestionIdsByBaseLocalId?.get(localId) : undefined;
		const firstSuggestionId: string | undefined = suggestionIds?.values().next().value;

		return {
			firstSuggestionId,
			isAnySuggestionSelected: Boolean(selectedSuggestion),
			isSelected: Boolean(selectedSuggestion && suggestionIds?.has(selectedSuggestion)),
			profiles: getUniqueAvatarProfiles(suggestionIds, suggestionById),
			shortDescription: firstSuggestionId
				? suggestionById?.get(firstSuggestionId)?.shortDescription
				: undefined,
			suggestionsCount: suggestionIds?.size ?? 0,
		};
	});

	// `SuggestionAvatarIndicator` ORs in one more trigger (hovering the icon directly), so it
	// only needs this single combined boolean from here.
	const isActive = isAnySuggestionSelected || isBlockActive;

	const isSelectingSuggestionRef = useRef(false);

	const onSelectSuggestion = useCallback(() => {
		if (isSelectingSuggestionRef.current) {
			return;
		}

		isSelectingSuggestionRef.current = true;

		const selectSuggestion = async (): Promise<void> => {
			try {
				const canCloseComment =
					(await api?.annotation?.actions.requestCloseInlineComment?.()) ?? true;

				if (!canCloseComment || !firstSuggestionId) {
					return;
				}

				api?.aiSuggestions?.actions.selectSuggestion(firstSuggestionId, {
					interactionPoint: 'card',
				});
			} finally {
				isSelectingSuggestionRef.current = false;
			}
		};

		void selectSuggestion();
	}, [api, firstSuggestionId]);

	const setHoveredSuggestion = useCallback(
		(id: string | undefined) => {
			api?.core?.actions.execute(({ tr }) => {
				tr.setMeta(aiSuggestionsPluginKey, { action: 'HOVER_SUGGESTION', id });
				return tr;
			});
		},
		[api],
	);

	const onMouseEnter = useCallback(
		() => setHoveredSuggestion(firstSuggestionId),
		[setHoveredSuggestion, firstSuggestionId],
	);
	const onMouseLeave = useCallback(() => setHoveredSuggestion(undefined), [setHoveredSuggestion]);
	const onFocus = useCallback(
		() => setHoveredSuggestion(firstSuggestionId),
		[setHoveredSuggestion, firstSuggestionId],
	);
	const onBlur = useCallback(() => setHoveredSuggestion(undefined), [setHoveredSuggestion]);

	const tooltip = useMemo(
		() => ({
			content:
				suggestionsCount > 1
					? formatMessage(aiSuggestionsMessages.iconButtonSuggestionsCountLabel, {
							count: suggestionsCount,
						}) ||
						(shortDescription ?? '')
					: (shortDescription ?? ''),
			position: 'top-start' as const,
			component: SuggestionTooltip,
			delay: 0,
			ignoreTooltipPointerEvents: true,
		}),
		[formatMessage, shortDescription, suggestionsCount],
	);

	if (!localId || !firstSuggestionId) {
		return null;
	}

	return (
		<span css={styles.container} style={{ anchorName: `--button-${localId}` }}>
			{
				// We keep the anchorName span rendered so that the suggestion card can reference it
				!isSelected && (
					<SuggestionAvatarIndicator
						isActive={isActive}
						profiles={profiles}
						tooltip={tooltip}
						label={tooltip.content}
						onClick={onSelectSuggestion}
						onMouseEnter={onMouseEnter}
						onMouseLeave={onMouseLeave}
						onFocus={onFocus}
						onBlur={onBlur}
					/>
				)
			}
		</span>
	);
}
